import React from 'react'
import { PowerPerformanceForms } from '../../types/mutual-fund-detail.types'
import LeafSVG from '../../icons/LeafSVG'
import LockSVG from '../../icons/LockSVG'
import { getPowerFormLabel, getPowerFormIconSVG } from '../../utils/formMetaData'
import { peerAnalyserColorTheme } from './utils'

export type ScoreStatus = 'IN_FORM' | 'ON_TRACK' | 'OFF_TRACK' | 'OUT_OF_FORM' | 'PERFORMANCE_LOCKED' | 'NOT_RATED'

interface ScoreProps {
  value: number | string
  status: ScoreStatus
  className?: string
}

const statusStyles: Record<ScoreStatus, { bg: string; text: string; svgColor: string }> = {
  IN_FORM: {
    bg: 'bg-status-in-form',
    text: 'text-status-text-in-form',
    svgColor: '#7ECA94'
  },
  ON_TRACK: {
    bg: 'bg-status-on-track',
    text: 'text-status-text-on-track',
    svgColor: '#90DCA6'
  },
  OFF_TRACK: {
    bg: 'bg-status-off-track',
    text: 'text-status-text-off-track',
    svgColor: '#EEBD80'
  },
  OUT_OF_FORM: {
    bg: 'bg-status-out-of-form',
    text: 'text-status-text-out-of-form',
    svgColor: '#F0AAA0'
  },
  PERFORMANCE_LOCKED: {
    bg: 'bg-[#F6F9F9]',
    text: 'text-[#6A6E6E]',
    svgColor: '#6A6E6E'
  },
  NOT_RATED: {
    bg: 'bg-[#F6F9F9]',
    text: 'text-grey40',
    svgColor: '#6A6E6E'
  }
}

const Score: React.FC<ScoreProps> = ({ value, status, className = '' }) => {
  const { text, svgColor, bg, lockSvgColor } = peerAnalyserColorTheme[status] || {}
  return (
    <div className='flex flex-row gap-[2px]'>
      <div
        className={`w-[50px] h-16 ${bg} ${className} relative rounded-sm flex-shrink-0`}
        aria-label={`Score: ${value}, Status: ${status}`}
      >
        {status === 'NOT_RATED' ? (
          <div className='inline-flex items-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
            <span className='relative left-[1px]'>
              <LeafSVG color={svgColor} />
            </span>
            <span
              className={`text-xs ${value ? text : 'text-grey40'} relative bottom-0 font-medium ${value ? '' : 'opacity-50'}`}
            >
              -
            </span>
            <span className='relative right-[1px]'>
              <LeafSVG color={svgColor} mirror />
            </span>
          </div>
        ) : status === 'PERFORMANCE_LOCKED' ? (
          <div className='inline-flex items-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
            <span className='relative left-[1px]'>
              <LeafSVG color={'#D1D6D6'} />
            </span>
            {/* unlockFundBottomSheets call disabled to avoid triggering bottom sheet */}
            <span className={`text-[13px] ${text} relative bottom-0`}>
              <LockSVG color={lockSvgColor} />
            </span>
            <span className='relative right-[1px]'>
              <LeafSVG color='#D1D6D6' mirror />
            </span>
          </div>
        ) : (
          <div className='inline-flex items-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
            <span className='relative left-[1px]'>
              <LeafSVG color={svgColor} />
            </span>
            {value || value === null ? (
              <span
                className={`text-[13px] ${value ? text : 'text-grey40'} relative bottom-0 font-medium ${value ? '' : 'opacity-50'}`}
              >
                {value || '-'}
              </span>
            ) : (
              <>
                {/* unlockFundBottomSheets call disabled to avoid triggering bottom sheet */}
                <span className={`text-[13px] ${text} relative bottom-0`}>
                  <LockSVG color={lockSvgColor} />
                </span>
              </>
            )}
            <span className='relative right-[1px]'>
              <LeafSVG color={svgColor} mirror />
            </span>
          </div>
        )}
      </div>
      {status === 'NOT_RATED' ? (
        <div className={`h-16 w-[108px] flex-shrink-0 flex items-center justify-center ${bg} rounded-sm `}>
          <div className='inline-flex overflow-hidden'>
            <span dangerouslySetInnerHTML={{ __html: getPowerFormIconSVG(status) }}></span>
            <span className={`text-xs leading-[12px] font-medium ${value ? text : 'text-grey40'} whitespace-nowrap`}>
              {getPowerFormLabel(status as PowerPerformanceForms)}
            </span>
          </div>
        </div>
      ) : status === 'PERFORMANCE_LOCKED' ? (
        <div className={`h-16 w-[108px] flex-shrink-0 flex items-center justify-center ${bg} rounded-sm `}>
          <div className='inline-flex gap-1 overflow-hidden'>
            {/* <span dangerouslySetInnerHTML={{ __html: getPowerFormIconSVG(status) }}></span> */}
            {/* unlockFundBottomSheets call disabled to avoid triggering bottom sheet */}
            <span className={`text-[13px] ${text} relative cursor-pointer`}>
              <LockSVG color={lockSvgColor} />
            </span>
          </div>
        </div>
      ) : (
        <div className={`h-16 w-[108px] flex-shrink-0 flex items-center justify-center ${bg} rounded-sm `}>
          <div className='inline-flex gap-1 overflow-hidden'>
            <span dangerouslySetInnerHTML={{ __html: getPowerFormIconSVG(status) }}></span>
            <span className={`text-xs leading-[12px] font-medium ${text} whitespace-nowrap`}>
              {getPowerFormLabel(status as PowerPerformanceForms)}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default Score
